import api, { clearAuthAndRedirect } from './api';
import type { User } from '../types';

export interface LoginRequest {
  email: string;
  password: string;
  mfaCode?: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  mfa_required?: boolean;
  mfa_token?: string;
  preferred_method?: 'totp' | 'email' | 'sms';
  available_methods?: string[];
}

export interface SSOProvider {
  id: string;
  provider_type: string;
  name: string;
}

export interface SSOLoginResponse {
  authorization_url: string;
  state: string;
}

// Storage key to track which storage is being used
const STORAGE_TYPE_KEY = 'auth_storage_type';

/**
 * Decode JWT token payload (without verification)
 */
function decodeJwtPayload(token: string): { exp?: number; [key: string]: unknown } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * Returns true if expired or invalid, false if still valid
 */
function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return true;
  // Add 10 second buffer to avoid edge cases
  const expiresAt = payload.exp * 1000;
  return Date.now() >= expiresAt - 10000;
}

/**
 * Get token expiry time in milliseconds
 */
function getTokenExpiryTime(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return null;
  return payload.exp * 1000;
}

/**
 * Get the appropriate storage based on rememberMe preference
 */
function getStorage(rememberMe?: boolean): Storage {
  if (rememberMe !== undefined) {
    return rememberMe ? localStorage : sessionStorage;
  }
  // Check which storage was used previously
  const storageType = localStorage.getItem(STORAGE_TYPE_KEY) || sessionStorage.getItem(STORAGE_TYPE_KEY);
  return storageType === 'session' ? sessionStorage : localStorage;
}

/**
 * Clear tokens from both storages
 */
function clearAllTokens(): void {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem(STORAGE_TYPE_KEY);
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  sessionStorage.removeItem(STORAGE_TYPE_KEY);
}

export const authService = {
  async login(credentials: LoginRequest): Promise<TokenResponse> {
    const response = await api.post<TokenResponse>('/api/v1/auth/login', credentials);
    return response.data;
  },

  async logout(): Promise<void> {
    // Always clear tokens first, regardless of API call result
    clearAllTokens();

    // Try to notify backend (optional - don't block on failure)
    try {
      await api.post('/api/v1/auth/logout');
    } catch {
      // Ignore errors - tokens are already cleared
    }
  },

  async verifyMfa(mfaToken: string, code: string, method?: string, trustDevice?: boolean): Promise<TokenResponse> {
    const response = await api.post<TokenResponse>('/api/v1/auth/mfa/verify', {
      code,
      mfa_token: mfaToken,
      method: method || 'totp',
      trust_device: trustDevice || false,
    });
    return response.data;
  },

  async sendMfaCode(mfaToken: string, method: string): Promise<void> {
    // Send MFA code via email or SMS (for non-TOTP methods)
    await api.post('/api/v1/auth/mfa/send-code', {
      mfa_token: mfaToken,
      method,
    });
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/api/v1/users/me');
    const data = response.data;
    // Transform snake_case from API to camelCase for frontend
    return {
      id: data.id,
      email: data.email,
      firstName: data.first_name,
      lastName: data.last_name,
      phone: data.phone,
      userType: data.user_type,
      tenantId: data.tenant_id,
      partnerId: data.partner_id,
      permissions: data.permissions || [],
    };
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/api/v1/auth/password/change', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  async requestPasswordReset(email: string): Promise<void> {
    await api.post('/api/v1/auth/password/forgot', { email });
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await api.post('/api/v1/auth/password/reset', {
      token,
      new_password: newPassword,
    });
  },

  setTokens(tokens: TokenResponse, rememberMe: boolean = true): void {
    // Clear any existing tokens from both storages first
    clearAllTokens();

    const storage = getStorage(rememberMe);
    storage.setItem('access_token', tokens.access_token);
    storage.setItem('refresh_token', tokens.refresh_token);
    // Track which storage we're using
    storage.setItem(STORAGE_TYPE_KEY, rememberMe ? 'local' : 'session');
  },

  getAccessToken(): string | null {
    // Check both storages
    return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  },

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');
  },

  isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    if (!token) return false;
    // Check if token is expired
    if (isTokenExpired(token)) {
      // Token expired, clear auth and return false
      clearAllTokens();
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      return false;
    }
    return true;
  },

  /**
   * Get remaining session time in milliseconds
   * Returns null if not authenticated or token is invalid
   */
  getSessionTimeRemaining(): number | null {
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    if (!token) return null;
    const expiryTime = getTokenExpiryTime(token);
    if (!expiryTime) return null;
    const remaining = expiryTime - Date.now();
    return remaining > 0 ? remaining : 0;
  },

  /**
   * Refresh the access token using the refresh token
   * Returns true if successful, false otherwise
   */
  async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await api.post<TokenResponse>('/api/v1/auth/refresh', {
        refresh_token: refreshToken,
      });

      // Determine which storage to use based on current storage
      const storage = localStorage.getItem('access_token') ? localStorage : sessionStorage;
      storage.setItem('access_token', response.data.access_token);
      storage.setItem('refresh_token', response.data.refresh_token);

      return true;
    } catch {
      return false;
    }
  },

  /**
   * Start a session expiry watcher that refreshes token before it expires
   * Returns a cleanup function to stop the watcher
   */
  startSessionExpiryWatcher(): () => void {
    let timeoutId: number | null = null;
    let isRefreshing = false;

    const checkAndSchedule = async () => {
      const remaining = authService.getSessionTimeRemaining();

      if (remaining === null) {
        // Not authenticated
        clearAuthAndRedirect();
        return;
      }

      // Refresh token 2 minutes before expiry
      const REFRESH_BUFFER_MS = 2 * 60 * 1000;

      if (remaining <= REFRESH_BUFFER_MS && !isRefreshing) {
        isRefreshing = true;
        const refreshed = await authService.refreshAccessToken();
        isRefreshing = false;

        if (!refreshed) {
          // Refresh failed, log out
          clearAuthAndRedirect();
          return;
        }
        // Token refreshed, continue watching
      }

      if (remaining <= 0) {
        // Token expired and couldn't refresh
        clearAuthAndRedirect();
        return;
      }

      // Schedule next check - check more frequently as expiry approaches
      const nextCheck = remaining > REFRESH_BUFFER_MS
        ? Math.min(remaining - REFRESH_BUFFER_MS, 60000)  // Check at most every minute
        : 10000; // Check every 10 seconds when close to expiry

      timeoutId = window.setTimeout(checkAndSchedule, nextCheck);
    };

    // Start checking
    checkAndSchedule();

    // Return cleanup function
    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  },

  // SSO Methods
  async getSSOProviders(tenantCode: string): Promise<SSOProvider[]> {
    const response = await api.get<{ providers: SSOProvider[] }>(
      `/api/v1/auth/sso/providers?tenant_code=${encodeURIComponent(tenantCode)}`
    );
    return response.data.providers;
  },

  async initiateSSOLogin(
    providerType: string,
    tenantCode: string,
    redirectUri: string
  ): Promise<SSOLoginResponse> {
    const endpoint =
      providerType === 'azure_ad' ? 'azure-ad' : providerType === 'google' ? 'google' : providerType;
    const response = await api.get<SSOLoginResponse>(
      `/api/v1/auth/sso/${endpoint}/login?tenant_code=${encodeURIComponent(tenantCode)}&redirect_uri=${encodeURIComponent(redirectUri)}`
    );
    return response.data;
  },

  async completeSSOLogin(providerType: string, code: string, state: string): Promise<TokenResponse> {
    const endpoint =
      providerType === 'azure_ad' ? 'azure-ad' : providerType === 'google' ? 'google' : providerType;
    const response = await api.post<TokenResponse>(`/api/v1/auth/sso/${endpoint}/callback`, {
      code,
      state,
    });
    return response.data;
  },

  /**
   * Initiate SSO login by provider ID (new hierarchical SSO)
   * This uses the configured SSO provider from the database
   */
  async initiateSSOLoginById(
    providerId: string,
    redirectUri: string
  ): Promise<SSOLoginResponse> {
    const response = await api.get<SSOLoginResponse>(
      `/api/v1/auth/sso/provider/${providerId}/login`,
      { params: { redirect_uri: redirectUri } }
    );
    return response.data;
  },

  /**
   * Complete SSO login by provider ID (new hierarchical SSO)
   */
  async completeSSOLoginById(
    providerId: string,
    code: string,
    state: string
  ): Promise<TokenResponse> {
    const response = await api.post<TokenResponse>(`/api/v1/auth/sso/provider/${providerId}/callback`, {
      code,
      state,
    });
    return response.data;
  },

  // ==================== MFA Methods ====================

  /**
   * Get user's MFA status
   */
  async getMfaStatus(): Promise<MfaStatus> {
    try {
      const response = await api.get<MfaStatus>('/api/v1/auth/mfa/status');
      const data = response.data;
      // Normalize response to support both old and new formats
      return {
        enabled: data.enabled ?? data.mfa_enabled ?? false,
        mfa_enabled: data.enabled ?? data.mfa_enabled ?? false,
        preferred_method: data.preferred_method,
        methods: data.methods || [],
        backup_codes_remaining: data.backup_codes_remaining ?? data.recovery_codes_remaining ?? 0,
        recovery_codes_remaining: data.backup_codes_remaining ?? data.recovery_codes_remaining ?? 0,
        phone_masked: data.phone_masked,
        backup_email: data.backup_email,
      };
    } catch {
      // Return default status if endpoint doesn't exist
      return {
        enabled: false,
        mfa_enabled: false,
        methods: [],
        backup_codes_remaining: 0,
        recovery_codes_remaining: 0,
      };
    }
  },

  /**
   * Initiate MFA setup (supports totp, email, sms)
   */
  async initiateMfaSetup(request: { method: string; phone?: string }): Promise<MfaSetupInitiateResponse> {
    const response = await api.post<MfaSetupInitiateResponse>(
      '/api/v1/auth/mfa/setup/initiate',
      request
    );
    return response.data;
  },

  /**
   * Setup TOTP authenticator app (legacy method)
   */
  async setupTotp(): Promise<{ secret: string; qr_code: string }> {
    const response = await api.post<MfaSetupInitiateResponse>(
      '/api/v1/auth/mfa/setup/initiate',
      { method: 'totp' }
    );
    return {
      secret: response.data.secret || '',
      qr_code: response.data.qr_code || response.data.qr_uri || '',
    };
  },

  /**
   * Verify MFA setup with code
   */
  async verifyMfaSetup(code: string, method: string): Promise<{ success: boolean; backup_codes: string[] }> {
    const response = await api.post<{ success: boolean; backup_codes?: string[] }>(
      '/api/v1/auth/mfa/setup/verify',
      { code, method }
    );
    return {
      success: response.data.success,
      backup_codes: response.data.backup_codes || [],
    };
  },

  /**
   * Verify TOTP setup with code (legacy method)
   */
  async verifyTotpSetup(code: string): Promise<{ success: boolean; backup_codes?: string[] }> {
    return this.verifyMfaSetup(code, 'totp');
  },

  /**
   * Disable MFA entirely (requires password and current code)
   */
  async disableMfa(password: string, code: string): Promise<void> {
    await api.post('/api/v1/auth/mfa/disable', { password, code });
  },

  /**
   * Disable an MFA method (legacy method)
   */
  async disableMfaMethod(methodType: string): Promise<void> {
    await api.post('/api/v1/auth/mfa/disable', { method: methodType });
  },

  /**
   * Regenerate backup codes (requires current MFA code)
   */
  async regenerateBackupCodes(code: string): Promise<{ backup_codes: string[] }> {
    const response = await api.post<{ backup_codes?: string[]; codes?: string[] }>(
      '/api/v1/auth/mfa/recovery-codes',
      { code }
    );
    return { backup_codes: response.data.backup_codes || response.data.codes || [] };
  },

  /**
   * Generate new recovery codes (legacy method)
   */
  async generateRecoveryCodes(): Promise<{ codes: string[] }> {
    const response = await api.post<{ codes: string[]; backup_codes?: string[] }>(
      '/api/v1/auth/mfa/recovery-codes'
    );
    return { codes: response.data.codes || response.data.backup_codes || [] };
  },

  /**
   * List trusted devices
   */
  async listTrustedDevices(): Promise<TrustedDevice[]> {
    const response = await api.get<{ devices: TrustedDevice[] } | TrustedDevice[]>(
      '/api/v1/auth/mfa/trusted-devices'
    );
    // Handle both array and object responses
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return response.data.devices || [];
  },

  /**
   * Remove a trusted device
   */
  async removeTrustedDevice(deviceId: string): Promise<void> {
    await api.delete(`/api/v1/auth/mfa/trusted-devices/${deviceId}`);
  },
};

// MFA Status interface for component compatibility
export interface MfaStatus {
  enabled: boolean;
  mfa_enabled?: boolean; // Legacy alias
  preferred_method?: 'totp' | 'email' | 'sms';
  methods: Array<{
    type: 'totp' | 'email' | 'sms';
    enabled: boolean;
    is_primary: boolean;
    verified: boolean;
    last_used?: string;
  }>;
  backup_codes_remaining: number;
  recovery_codes_remaining?: number; // Legacy alias
  phone_masked?: string;
  backup_email?: string;
}

export interface MfaSetupInitiateResponse {
  secret?: string;
  qr_uri?: string;
  qr_code?: string;
  email_masked?: string;
  phone_masked?: string;
}

export interface TrustedDevice {
  id: string;
  device_name: string;
  trusted_until: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export default authService;
